import { useState } from 'react';
import type { FormEvent } from 'react';

import type { GameStatus } from '@perplexicon/api-client';

import {
  Button,
  Card,
  ErrorState,
  Input,
  LoadingState,
  PageHeader,
  PageLayout,
  SectionHeading,
  StatusMessage,
} from '../../components/index.js';
import {
  useAdminGames,
  useAdminHealth,
  useAdminSessionInspection,
  useSetGameStatus,
} from '../../lib/admin.js';
import { getErrorMessage } from '../../lib/errors.js';
import { formatDateTime } from '../../lib/format-date.js';

const GAME_STATUSES: GameStatus[] = ['active', 'beta', 'maintenance', 'disabled'];

function GameStatusControls() {
  const games = useAdminGames();
  const update = useSetGameStatus();
  if (games.isPending) return <LoadingState label="Loading game controls…" />;
  if (games.isError) {
    return (
      <ErrorState message={getErrorMessage(games.error)} onRetry={() => void games.refetch()} />
    );
  }
  return (
    <div className="grid gap-3">
      {games.data.map((game) => (
        <Card
          key={game.gameId}
          className="flex flex-col justify-between gap-4 tablet:flex-row tablet:items-center"
        >
          <div className="min-w-0">
            <h3 className="font-semibold text-textPrimary">{game.name}</h3>
            <p className="break-all text-sm text-textSecondary">
              {game.gameId} · version {game.currentVersion}
              {!game.registered && ' · code not registered'}
            </p>
          </div>
          <label className="flex min-w-0 flex-col gap-1 text-sm font-semibold text-textPrimary tablet:w-48">
            Operational status
            <select
              className="min-h-touch rounded-md border border-border bg-parchment px-3 py-2 text-textPrimary shadow-inset"
              value={game.status}
              disabled={update.isPending}
              onChange={(event) =>
                update.mutate({ gameId: game.gameId, status: event.target.value as GameStatus })
              }
            >
              {GAME_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </label>
        </Card>
      ))}
      {update.isError && <ErrorState message={getErrorMessage(update.error)} />}
      {update.isSuccess && (
        <StatusMessage tone="success">
          Game status updated. Existing sessions remain playable.
        </StatusMessage>
      )}
    </div>
  );
}

function HealthDetail() {
  const health = useAdminHealth();
  if (health.isPending) return <LoadingState label="Checking platform health…" />;
  if (health.isError) {
    return (
      <ErrorState message={getErrorMessage(health.error)} onRetry={() => void health.refetch()} />
    );
  }
  const { counts } = health.data;
  return (
    <Card>
      <dl className="grid grid-cols-2 gap-4 tablet:grid-cols-4">
        {Object.entries({
          Users: counts.users,
          Sessions: counts.sessions,
          Active: counts.activeSessions,
          Games: counts.games,
        }).map(([label, value]) => (
          <div key={label}>
            <dt className="text-sm text-textSecondary">{label}</dt>
            <dd className="text-2xl font-semibold text-textPrimary">{value}</dd>
          </div>
        ))}
      </dl>
      <p className="mt-4 text-sm text-textSecondary">
        Database {health.data.database}; process uptime {health.data.uptimeSeconds}s. Checked{' '}
        {formatDateTime(health.data.checkedAt)}.
      </p>
    </Card>
  );
}

function SessionInspector() {
  const [draftId, setDraftId] = useState('');
  const [sessionId, setSessionId] = useState('');
  const inspection = useAdminSessionInspection(sessionId);
  function submit(event: FormEvent) {
    event.preventDefault();
    setSessionId(draftId.trim());
  }
  return (
    <div className="grid gap-4">
      <form onSubmit={submit} className="flex flex-col items-end gap-3 tablet:flex-row">
        <div className="w-full flex-1">
          <Input
            label="Session ID"
            value={draftId}
            onChange={(event) => setDraftId(event.target.value)}
            placeholder="UUID"
            required
          />
        </div>
        <Button type="submit">Inspect session</Button>
      </form>
      {inspection.isFetching && <LoadingState label="Loading authoritative session record…" />}
      {inspection.isError && <ErrorState message={getErrorMessage(inspection.error)} />}
      {inspection.data && (
        <Card variant="inset">
          <p className="mb-3 text-sm font-semibold text-warning">
            Restricted authoritative state. Every access is recorded by the API.
          </p>
          <pre className="max-h-[32rem] max-w-full overflow-auto whitespace-pre-wrap break-words text-xs text-textPrimary">
            {JSON.stringify(inspection.data, null, 2)}
          </pre>
        </Card>
      )}
    </div>
  );
}

export function AdminPage() {
  return (
    <PageLayout>
      <PageHeader
        title="Administration"
        description="Platform maintenance, operational game controls, and access-logged diagnostics."
      />
      <section className="grid gap-4" aria-labelledby="admin-health-heading">
        <SectionHeading>
          <span id="admin-health-heading">Health</span>
        </SectionHeading>
        <HealthDetail />
      </section>
      <section className="grid gap-4" aria-labelledby="admin-games-heading">
        <SectionHeading>
          <span id="admin-games-heading">Game availability</span>
        </SectionHeading>
        <GameStatusControls />
      </section>
      <section className="grid gap-4" aria-labelledby="admin-session-heading">
        <SectionHeading>
          <span id="admin-session-heading">Session inspection</span>
        </SectionHeading>
        <SessionInspector />
      </section>
    </PageLayout>
  );
}
